// TLTB Partnership page i18n — mirrors backend routes/tltb_i18n.py.
// Same translation policy: proper nouns (TLTB, Mataqali, FIRCA, RBF, FRCS, FNPF, EIA,
// Investor Permit, Joint Venture, Bula Real Estate Fiji) intentionally retained verbatim
// across all 3 languages — they are the official terms used by Fiji's government even
// within Fijian and Hindi documents.

const EN = {
    nativeLabel: "English",
    // Hero
    heroBadge: "Strategic Partnership Pack · iTaukei Land Trust Board",
    heroTitlePre: "A trusted partner for ",
    heroTitleAccent: "iTaukei landowners",
    heroSubtitle:
        "Bringing vetted foreign capital to TLTB-managed native land — with Mataqali in lifelong equity, all compliance handled, and a transparent intermediary protecting both sides.",
    btnDownloadPdf: "Download partnership pack (PDF)",
    btnScheduleMeeting: "Schedule executive meeting",
    pdfLanguageLabel: "PDF & PAGE LANGUAGE",
    pdfLanguageA11y: "Choose page and PDF language",
    btnShare: "Share",
    shareCopiedToast: "Localised share message copied — paste into LinkedIn, WhatsApp, or email.",
    shareWhatsapp: "Share on WhatsApp",
    shareLinkedin: "Share on LinkedIn",
    shareCopy: "Copy share message",
    // Stats
    stat1Lbl: "Of Fiji's land is iTaukei",
    stat2Lbl: "Avg landowner uplift on JV deals",
    stat3Lbl: "Government agencies coordinated",
    stat4Lbl: "Direct buyer-seller contact (we route all)",
    // Opportunity
    oppKicker: "The opportunity",
    oppTitlePre: "TLTB-managed land deserves more than ",
    oppTitleAccent: "one-time lease premiums.",
    oppP1:
        "For decades, iTaukei native land has been monetised through fixed-term leases — agricultural, commercial, residential — at rents that reset every five to ten years. The model produces stable but small income for landowner Mataqali, with little participation in project upside. Tourism, hospitality, and renewable-energy projects in particular generate returns that dwarf the lease line item.",
    oppP2:
        "At the same time, foreign investors — North America, Australia, NZ, India, increasingly the Middle East — actively seek emerging-market real-asset exposure. They have the capital and the risk appetite. They lack a credible partner to:",
    oppBullets: [
        "Pre-vet TLTB-leased opportunities and Mataqali consents.",
        "Handle Investor Permits, RBF capital inflow, and FIRCA approvals.",
        "Coordinate council planning, environmental impact, and tax incentives.",
        "Hold escrow on partnership cash flows and protect Mataqali equity.",
    ],
    // JV model
    jvKicker: "The Bula Joint Venture model",
    jvTitlePre: "Mataqali keep equity. ",
    jvTitleAccent: "For the life of the asset.",
    jvIntro:
        "We replace one-time lease premiums with an ownership structure that gives the landowner unit a meaningful, in-perpetuity participation in the asset. The capital partner brings cash and expertise; we structure, govern, and operate.",
    jvColElement: "Element",
    jvColMataqali: "Mataqali",
    jvColCapital: "Capital partner",
    jvRowEquityL: "Equity split", jvRowEquityM: "15–30%", jvRowEquityC: "70–85%",
    jvRowCashL: "Cash at signing", jvRowCashM: "Goodwill payment", jvRowCashC: "Capital contribution",
    jvRowIncomeL: "Ongoing income", jvRowIncomeM: "Quarterly distributions", jvRowIncomeC: "Quarterly distributions",
    jvRowDecisionL: "Decision rights", jvRowDecisionM: "Reserved-matters seat", jvRowDecisionC: "Operational majority",
    jvRowExitL: "Exit", jvRowExitM: "Pro-rata on sale", jvRowExitC: "Pro-rata on sale",
    // What we handle
    wwhKicker: "What we handle (so TLTB doesn't)",
    wwhTitlePre: "Every investor we admit is ",
    wwhTitleAccent: "already cleared.",
    wwhIntro:
        "Each foreign investor moves through a 12-step service pipeline before they ever see a TLTB-fronted opportunity. TLTB sees only investor profiles that are compliance-cleared, capital-verified, and Mataqali-protocol-briefed.",
    wwhBlocks: [
        { label: "Investor screening", desc: "KYC, source-of-funds, capital verification — before they ever see a TLTB-fronted opportunity." },
        { label: "RBF capital inflow", desc: "Approval to land foreign-source capital + repatriation pathway designed at signing." },
        { label: "Entity & banking", desc: "Companies Office + FRCS TIN + FNPF + bank introduction in one workflow." },
        { label: "Council planning", desc: "Direct relationships at Suva, Nadi, Lautoka, and Sigatoka councils." },
        { label: "Environment Impact", desc: "EIA coordination for coastal & resort projects." },
        { label: "Tax incentives", desc: "Tourism Hotel Aid Act, SLIP, sectoral allowances filed and tracked." },
        { label: "Mataqali consent", desc: "Landowner unit briefing, consent coordination, and ongoing relationship management." },
        { label: "Investor Permit", desc: "Department of Immigration end-to-end, including dependent visas." },
    ],
    // Governance
    govKicker: "Governance & protections",
    govTitle: "Built so neither side is exposed.",
    govCalloutTitle: "Platform-as-Intermediary",
    govCalloutBody:
        "Bula Real Estate Fiji is the official intermediary for every conversation, document, and offer. Direct buyer-seller contact is not allowed on the platform. This protects Mataqali landowners from unsolicited approach and ensures every investor interaction is logged, vetted, and routed.",
    govItems: [
        { title: "Mataqali equity is non-dilutable", body: "Reserved matters protections written into JV deed." },
        { title: "Quarterly distributions held in escrow", body: "Released to Mataqali bank account on schedule." },
        { title: "Independent annual audit", body: "Audit-ready documentation throughout the life of the partnership." },
        { title: "TLTB observer rights", body: "Standing observer seat at every JV board meeting." },
    ],
    // Engagement
    engageKicker: "How we propose to engage",
    engageTitlePre: "Four steps. ",
    engageTitleAccent: "Non-binding throughout.",
    engageSteps: [
        { num: "01", title: "Briefing", desc: "60-minute introduction with TLTB executive leadership and any advisors you'd like present." },
        { num: "02", title: "Pilot", desc: "Co-design a single, low-risk pilot opportunity end-to-end (likely tourism or agricultural)." },
        { num: "03", title: "MOU", desc: "Non-binding memorandum recording principles: investor screening, Mataqali equity, governance, dispute resolution." },
        { num: "04", title: "Programme", desc: "Subject to pilot success, multi-year programme of co-developed opportunities with quarterly executive review." },
    ],
    // Track record
    trackBody: "Across our active marketplace and Joint Venture portal, average uplift on JV-structured landowner positions is",
    trackBodyAccent: "+138% above original asking",
    trackBodySuffix: " — reflecting the participation premium our model delivers vs. straight lease assignments.",
    trackCta: "See the Hall of Fame",
    // Final CTA
    finalKicker: "Next step",
    finalTitlePre: "Let's meet — ",
    finalTitleAccent: "confidential, non-binding.",
    finalP:
        "We'll share the model, the safeguards, and a sample pipeline at any depth your team requires. Bring whoever you'd like — board members, advisors, legal counsel.",
    btnRequestMeeting: "Request executive meeting",
    // Share message templates (LinkedIn / WhatsApp body)
    shareMessage:
        "I came across the Bula Real Estate Fiji partnership pack for TLTB — they bring vetted foreign capital to TLTB-managed native land, keep Mataqali in 15–30% lifelong equity, and handle the full Investor Permit / RBF / FIRCA stack. Worth a look:",
};


const FJ = {
    ...EN,
    nativeLabel: "Vakaviti",
    heroBadge: "Mataqali ni Veiwekani Vakatuvaki · iTaukei Land Trust Board",
    heroTitlePre: "Itokani vakadinata vei na ",
    heroTitleAccent: "iTaukei dauni qele",
    heroSubtitle:
        "Sa kau mai na ilavo mai vanua tani sa vakatovolei tu — me ra dau na Mataqali ena vanua ni vakaitavi tu dei tikoga, sa qarauni na ka kece, ka sa dua na itokani savasava ni veitarogi me ra qarauni vata.",
    btnDownloadPdf: "Taubale na Pack ni Veiwekani (PDF)",
    btnScheduleMeeting: "Vakarautaki na soqo",
    pdfLanguageLabel: "VOSA NI TABANA & PDF",
    pdfLanguageA11y: "Digia na vosa ni tabana kei na PDF",
    btnShare: "Wasea",
    shareCopiedToast: "Sa cocoroki na itukutuku — biuti ki LinkedIn, WhatsApp, se na imeli.",
    shareWhatsapp: "Wasea ki WhatsApp",
    shareLinkedin: "Wasea ki LinkedIn",
    shareCopy: "Cocoroki na itukutuku",
    stat1Lbl: "Ni vanua e Viti sa iTaukei",
    stat2Lbl: "Toso ni Mataqali ena JV",
    stat3Lbl: "Tabana ni Matanitu sa qaravi",
    stat4Lbl: "Veitarogi vakadua (keimami qaravi)",
    oppKicker: "Na vanua ni vakaitavi",
    oppTitlePre: "Na vanua ni TLTB e dodonu me ",
    oppTitleAccent: "uasivi cake mai na lisi dua walega.",
    oppP1:
        "Sa dau lava ga na lisi ni vanua iTaukei me bula vakaqele, vakavolivolitaki, se vakavale, ena isau ni veisiga e veisaui ena lima se tini na yabaki. E vinaka vakaila na isau ki na Mataqali, ia e sega ni vakaiwasewase ena uvi ni cakacaka. Na cakacaka ni saravanua, lewenivanua kei na livaliva sa cavuti vakalevu na uvi e levu cake.",
    oppP2:
        "Ena gauna vata, na ilavo mai Amerika, Ositerelia, NZ, Idia, kei na vanua vou era vakasaqaqara tiko mai ena ilavo. Era tu na ilavo. Era sega ga na itokani ni:",
    oppBullets: [
        "Vakatovolei na vanua e lisi tiko vei TLTB kei na vakadonu ni Mataqali.",
        "Qarava na pemiti ni dauveiqaravi, RBF, kei na FIRCA.",
        "Veitokoni ena naki ni koro, vakatusa rerevaki, kei na veivakanaki vakatevoro.",
        "Maroroya na ilavo ena ekisiro me taqomaki kina na vakaitavi ni Mataqali.",
    ],
    jvKicker: "Na ivakarau ni Joint Venture e Bula",
    jvTitlePre: "E dei tikoga vei na Mataqali. ",
    jvTitleAccent: "Ena bula taucoko ni vanua.",
    jvIntro:
        "Keimami sosomitaka na isau dua walega ena dua na ivakarau ni taukeni e dei tikoga vei na Mataqali. Na itokani ilavo e kau mai na ilavo kei na kila; keimami vakatuva, qarauni, ka valavala.",
    jvColElement: "Tikina",
    jvColCapital: "Itokani ilavo",
    jvRowEquityL: "Wasewase ni vakaitavi",
    jvRowCashL: "Ilavo ena saini", jvRowCashM: "Isau ni veivakaturi", jvRowCashC: "Ilavo vakaitavi",
    jvRowIncomeL: "Ilavo tikoga", jvRowIncomeM: "Wasei vakavula", jvRowIncomeC: "Wasei vakavula",
    jvRowDecisionL: "Ka ni veidigi", jvRowDecisionM: "Tikotiko ni vosa", jvRowDecisionC: "Veiqaravi raraba",
    jvRowExitL: "Lakolako", jvRowExitM: "Wasei vakaira ni volivoli", jvRowExitC: "Wasei vakaira ni volivoli",
    wwhKicker: "Na ka keimami qaravi (me sega ni baleti na TLTB)",
    wwhTitlePre: "Na ilavo kece keimami curu kina sa ",
    wwhTitleAccent: "oti ni vakatovolei.",
    wwhIntro:
        "Na ilavo kece keimami curu kina ena vanua ni TLTB sa oti ni vakatovolei ena keimami yavu ni veiqaravi e 12 na isteba. E kena ibalebale ni TLTB ena raica ga na ilavo sa savasava, sa vakadeitaki, ka sa rai na ivakarau vaka-Mataqali.",
    wwhBlocks: [
        { label: "Veivakatovolei", desc: "Vakadinata ilavo, KYC, vutuni-ilavo — ni bera ni ra raica e dua na vanua ni TLTB." },
        { label: "RBF curu ilavo", desc: "Vakadonu ni curu mai na ilavo + sala ni vakasuka tara mai na saini." },
        { label: "Kabani & biloko", desc: "Companies Office + FRCS TIN + FNPF + biloko ena dua na sala." },
        { label: "Naki ni koro", desc: "Itokatoka kei Suva, Nadi, Lautoka, Sigatoka." },
        { label: "Tabana ni Vanua", desc: "EIA me baleti na vanua ni saravanua." },
        { label: "Veivuke vakatevoro", desc: "Tourism Hotel Aid Act, SLIP, kei na so tale sa volai." },
        { label: "Vakadonu ni Mataqali", desc: "Vakatatura kei na qaravi tikoga ni Mataqali." },
        { label: "Pemiti ni Dauveiqaravi", desc: "Tabana ni Vakauruci taudua, kei na visa ni vuvale." },
    ],
    govKicker: "Veiqaravi & taqomaki",
    govTitle: "Sa vakatuvaki me sega ni baleti rua na yasana.",
    govCalloutTitle: "Yavu-itokani",
    govCalloutBody:
        "Na Bula Real Estate Fiji e dauveiqaravi ena veitalanoa kece, ivola, kei na vakatutu. E sega ni dodonu na veitarogi vakadua ena yavu. E taqomaki kina na Mataqali, ka sa volai ka qarauni na cakacaka kece.",
    govItems: [
        { title: "Vakaitavi ni Mataqali e sega ni rawa ni vakalailaitaki", body: "Taqomaki ena ivola ni JV." },
        { title: "Wasei vakavula maroroi ena ekisiro", body: "Soli ki na biloko ni Mataqali ena gauna donu." },
        { title: "Veidikevi yabaki vakatabakidua", body: "Ivola ni veidikevi rawarawa." },
        { title: "Dodonu ni TLTB me sara", body: "Tikotiko ni TLTB ena soqo ni JV." },
    ],
    engageKicker: "Sa rawa ni keimami veiqaravi vakacava",
    engageTitlePre: "Va na isteba. ",
    engageTitleAccent: "Sega ni vakadeitaki ena gauna kece.",
    engageSteps: [
        { num: "01", title: "Ivakatutu", desc: "60 na miniti me veitalanoa kei na lewe ni TLTB kei kemudou veidauloma." },
        { num: "02", title: "Tovolea", desc: "Cakacaka vata ena dua na vanua dua walega (saravanua se vakaqele) me vakatuvai vata kina na JV." },
        { num: "03", title: "MOU", desc: "Memo sega ni dei me volai kina na yavu: vakatovolei, vakaitavi, taqomaki, veivosaki ni veisei." },
        { num: "04", title: "Yavu raraba", desc: "Ke vinaka na ka ni tovolei, dua na yabaki vuqa ni veicakacakavata kei na veidikevi vakavula." },
    ],
    trackBody: "Ena keimami marketplace kei na portal ni Joint Venture, na uvi ni Mataqali e tubu ena",
    trackBodyAccent: "+138% mai na isau taumada",
    trackBodySuffix: " — e kena ivakaraitaki ni uvi ni keimami ivakarau ni vakaitavi.",
    trackCta: "Raica na Hall of Fame",
    finalKicker: "Itabataba e tarava",
    finalTitlePre: "Me da sota — ",
    finalTitleAccent: "vakatabu, sega ni vakadeitaki.",
    finalP:
        "Keimami na wasea na ivakarau, na taqomaki, kei na vanua sa tu rawa ena dua na sara. Kauti ira mai o ira o via — na lewe, na veidauloma, na lewe ni lawa.",
    btnRequestMeeting: "Kerea na soqo",
    shareMessage:
        "Ka'u sa raica na pack ni veiwekani ni Bula Real Estate Fiji vei TLTB — era kau mai na ilavo sa vakatovolei mai vanua tani ki na vanua ni TLTB, dei kina vei na Mataqali na 15–30% vakaitavi tikoga, ka qarava na Investor Permit / RBF / FIRCA kece. Raica:",
};


const HI = {
    ...EN,
    nativeLabel: "हिन्दी",
    heroBadge: "रणनीतिक भागीदारी पैक · iTaukei Land Trust Board",
    heroTitlePre: "एक भरोसेमंद साझेदार ",
    heroTitleAccent: "iTaukei भूस्वामियों के लिए",
    heroSubtitle:
        "TLTB-प्रबंधित मूल भूमि पर सत्यापित विदेशी पूँजी लाना — Mataqali को आजीवन इक्विटी, सभी अनुपालन का प्रबंधन, और दोनों पक्षों की रक्षा करने वाला पारदर्शी मध्यस्थ।",
    btnDownloadPdf: "भागीदारी पैक डाउनलोड करें (PDF)",
    btnScheduleMeeting: "कार्यकारी बैठक तय करें",
    pdfLanguageLabel: "पृष्ठ और PDF भाषा",
    pdfLanguageA11y: "पृष्ठ और PDF भाषा चुनें",
    btnShare: "साझा करें",
    shareCopiedToast: "स्थानीय संदेश कॉपी हो गया — LinkedIn, WhatsApp या ईमेल में पेस्ट करें।",
    shareWhatsapp: "WhatsApp पर साझा करें",
    shareLinkedin: "LinkedIn पर साझा करें",
    shareCopy: "संदेश कॉपी करें",
    stat1Lbl: "फिजी की भूमि iTaukei है",
    stat2Lbl: "JV सौदों पर औसत भूस्वामी वृद्धि",
    stat3Lbl: "सरकारी एजेंसियाँ समन्वित",
    stat4Lbl: "सीधा क्रेता-विक्रेता संपर्क (हम राउट करते हैं)",
    oppKicker: "अवसर",
    oppTitlePre: "TLTB-प्रबंधित भूमि एकमुश्त ",
    oppTitleAccent: "पट्टा प्रीमियम से अधिक की हकदार है।",
    oppP1:
        "दशकों से iTaukei मूल भूमि निश्चित-अवधि पट्टों के माध्यम से उपयोग होती रही है — कृषि, वाणिज्यिक, आवासीय — हर पाँच से दस वर्ष में किराये के साथ। यह मॉडल Mataqali को स्थिर परंतु छोटी आय देता है, परियोजना के लाभ में भागीदारी नहीं देता। पर्यटन, आतिथ्य और अक्षय-ऊर्जा परियोजनाएँ ऐसे रिटर्न देती हैं जो पट्टे की आय से कई गुना अधिक होते हैं।",
    oppP2:
        "साथ ही, विदेशी निवेशक — उत्तर अमेरिका, ऑस्ट्रेलिया, NZ, भारत, और बढ़ते हुए मध्य पूर्व — सक्रिय रूप से उभरते बाज़ार की वास्तविक-संपत्ति में एक्सपोज़र चाहते हैं। पूँजी और जोखिम-क्षमता है। उन्हें ऐसे साझेदार की कमी है जो:",
    oppBullets: [
        "TLTB-पट्टे के अवसरों और Mataqali सहमति की पूर्व-जाँच करे।",
        "Investor Permits, RBF पूँजी प्रवाह, और FIRCA अनुमोदन संभाले।",
        "परिषद् योजना, पर्यावरणीय प्रभाव, और कर प्रोत्साहन समन्वय करे।",
        "साझेदारी के नकद-प्रवाह पर एस्क्रो रखे और Mataqali इक्विटी की रक्षा करे।",
    ],
    jvKicker: "Bula Joint Venture मॉडल",
    jvTitlePre: "Mataqali इक्विटी रखते हैं। ",
    jvTitleAccent: "परियोजना के पूरे जीवनकाल के लिए।",
    jvIntro:
        "हम एकमुश्त पट्टा प्रीमियम को ऐसी स्वामित्व संरचना से बदलते हैं जो भूस्वामी इकाई को परियोजना में स्थायी, सार्थक भागीदारी देती है। पूँजी साझेदार नकद और विशेषज्ञता लाता है; हम संरचना, शासन और संचालन करते हैं।",
    jvColElement: "तत्व",
    jvColCapital: "पूँजी साझेदार",
    jvRowEquityL: "इक्विटी विभाजन",
    jvRowCashL: "हस्ताक्षर पर नकद", jvRowCashM: "सद्भावना भुगतान", jvRowCashC: "पूँजी योगदान",
    jvRowIncomeL: "नियमित आय", jvRowIncomeM: "त्रैमासिक वितरण", jvRowIncomeC: "त्रैमासिक वितरण",
    jvRowDecisionL: "निर्णय अधिकार", jvRowDecisionM: "आरक्षित मामलों की सीट", jvRowDecisionC: "परिचालन बहुमत",
    jvRowExitL: "निकास", jvRowExitM: "बिक्री पर समानुपातिक", jvRowExitC: "बिक्री पर समानुपातिक",
    wwhKicker: "हम क्या संभालते हैं (ताकि TLTB को न करना पड़े)",
    wwhTitlePre: "हर निवेशक जिसे हम लाते हैं ",
    wwhTitleAccent: "पहले से जाँचा हुआ है।",
    wwhIntro:
        "हर विदेशी निवेशक हमारी 12-चरण सेवा पाइपलाइन से गुज़रता है इससे पहले कि TLTB-संबद्ध अवसर देखे। TLTB को केवल वही प्रोफ़ाइल दिखती हैं जो अनुपालन-मुक्त, पूँजी-सत्यापित, और Mataqali-प्रोटोकॉल-ब्रीफ़ हों।",
    wwhBlocks: [
        { label: "निवेशक स्क्रीनिंग", desc: "KYC, स्रोत-निधि, पूँजी सत्यापन — TLTB अवसर देखने से पहले।" },
        { label: "RBF पूँजी प्रवाह", desc: "विदेशी पूँजी लाने और प्रत्यावर्तन मार्ग की मंज़ूरी।" },
        { label: "इकाई और बैंकिंग", desc: "Companies Office + FRCS TIN + FNPF + बैंक परिचय।" },
        { label: "परिषद् योजना", desc: "Suva, Nadi, Lautoka, Sigatoka में सीधे संबंध।" },
        { label: "पर्यावरण प्रभाव", desc: "तटीय और रिज़ॉर्ट परियोजनाओं के लिए EIA समन्वय।" },
        { label: "कर प्रोत्साहन", desc: "Tourism Hotel Aid Act, SLIP, क्षेत्रीय भत्ते दाखिल।" },
        { label: "Mataqali सहमति", desc: "भूस्वामी इकाई ब्रीफ़िंग, सहमति समन्वय, निरंतर प्रबंधन।" },
        { label: "Investor Permit", desc: "Department of Immigration पूर्ण सेवा, आश्रित वीज़ा सहित।" },
    ],
    govKicker: "शासन और सुरक्षा",
    govTitle: "इस तरह बनाया कि कोई पक्ष असुरक्षित न हो।",
    govCalloutTitle: "मंच-सह-मध्यस्थ",
    govCalloutBody:
        "Bula Real Estate Fiji हर बातचीत, दस्तावेज़ और प्रस्ताव के लिए आधिकारिक मध्यस्थ है। मंच पर सीधा क्रेता-विक्रेता संपर्क अनुमत नहीं है। यह Mataqali भूस्वामियों की रक्षा करता है और हर निवेशक संवाद को लॉग, सत्यापित, और राउट करता है।",
    govItems: [
        { title: "Mataqali इक्विटी कमजोर नहीं की जा सकती", body: "JV डीड में आरक्षित मामलों की सुरक्षा।" },
        { title: "त्रैमासिक वितरण एस्क्रो में रखे जाते हैं", body: "निर्धारित समय पर Mataqali बैंक खाते में जारी।" },
        { title: "स्वतंत्र वार्षिक ऑडिट", body: "साझेदारी के पूरे जीवनकाल में ऑडिट-तैयार दस्तावेज़।" },
        { title: "TLTB पर्यवेक्षक अधिकार", body: "हर JV बोर्ड बैठक में स्थायी पर्यवेक्षक सीट।" },
    ],
    engageKicker: "हम कैसे जुड़ने का प्रस्ताव रखते हैं",
    engageTitlePre: "चार चरण। ",
    engageTitleAccent: "हर चरण गैर-बाध्यकारी।",
    engageSteps: [
        { num: "01", title: "ब्रीफ़िंग", desc: "TLTB कार्यकारी नेतृत्व और आपके पसंदीदा सलाहकारों के साथ 60-मिनट का परिचय।" },
        { num: "02", title: "पायलट", desc: "एक कम-जोखिम पायलट अवसर का संयुक्त डिज़ाइन (संभवतः पर्यटन या कृषि)।" },
        { num: "03", title: "MOU", desc: "गैर-बाध्यकारी ज्ञापन: निवेशक स्क्रीनिंग, Mataqali इक्विटी, शासन, विवाद-निवारण।" },
        { num: "04", title: "कार्यक्रम", desc: "पायलट सफलता पर बहु-वर्षीय सह-विकसित अवसर कार्यक्रम, त्रैमासिक TLTB समीक्षा के साथ।" },
    ],
    trackBody: "हमारे सक्रिय मार्केटप्लेस और Joint Venture पोर्टल पर JV-संरचित भूस्वामी पदों पर औसत वृद्धि",
    trackBodyAccent: "मूल माँग से +138% ऊपर",
    trackBodySuffix: " है — जो हमारे मॉडल का प्रतिफल है।",
    trackCta: "Hall of Fame देखें",
    finalKicker: "अगला कदम",
    finalTitlePre: "मिलते हैं — ",
    finalTitleAccent: "गोपनीय, गैर-बाध्यकारी।",
    finalP:
        "हम मॉडल, सुरक्षा-उपाय, और एक नमूना पाइपलाइन किसी भी गहराई पर साझा करेंगे। आप जिन्हें चाहें ले आएँ — बोर्ड सदस्य, सलाहकार, क़ानूनी सलाह।",
    btnRequestMeeting: "कार्यकारी बैठक का अनुरोध करें",
    shareMessage:
        "मुझे Bula Real Estate Fiji का TLTB भागीदारी पैक मिला — वे TLTB-प्रबंधित मूल भूमि पर सत्यापित विदेशी पूँजी लाते हैं, Mataqali को 15–30% आजीवन इक्विटी देते हैं, और पूरा Investor Permit / RBF / FIRCA अनुपालन संभालते हैं। देखिए:",
};


const TRANSLATIONS = { en: EN, fj: FJ, hi: HI };

export const TLTB_LANGS = [
    { code: "en", label: "English" },
    { code: "fj", label: "Vakaviti (Fijian)" },
    { code: "hi", label: "हिन्दी (Hindi)" },
];

export function tltbT(lang) {
    return TRANSLATIONS[lang] || EN;
}
